import React from "react";
import styled from "styled-components";
import colours from "../../lib/colours";
import TextButtonWithArrow from "../atoms/TextButtonWithArrow";
import Callout from "../molecules/Callout";
import { ReactComponent as AlertIcon } from "../../assets/svg/alert.svg";

const TableContainer = styled.div`
  display: inline-block;
  margin: 10px 0;
  overflow-x: auto;
  overflow-y: hidden;
  width: 100%;
`;

const TableBody = styled.tbody`
  letter-spacing: -0.01875rem;
`;

const TableHeader = styled.th`
  background: ${colours.th1l1};
  border: 0.125rem solid ${colours.white};
  border-left: none;
  border-right: none;
  color: ${colours.white};
  font-size: 13px;
  font-weight: 400;
  padding: 8px;
  text-align: left;
  text-transform: uppercase;
`;

const TableRow = styled.tr`
  color: ${colours.darkerGrey};
  font-size: 13px;
  font-weight: 400;
`;
const StandardTableRow = styled(TableRow)`
  background: ${colours.offWhite};
`;

const HoverTableRow = styled(StandardTableRow)`
  :hover {
    background: ${colours.th1l3};
  }
`;

const HighlightedTableRow = styled(TableRow)`
  background: ${colours.th3l1};
`;

const WarningTableRow = styled(TableRow)`
  background: ${colours.th3l1};
  padding: 0;
`;

const TableCell = styled.td`
  border-top: 0.125rem solid ${colours.white};
  border-left: none;
  border-right: none;
  padding: 8px;
`;

const TableCellHighlighted = styled(TableCell)`
  padding: 4px;
  width: 14px;
`;

const buttonCellStyle = {
  padding: 0,
};

const highlightIconStyle: any = {
  height: "13px",
  position: "relative",
  top: "2px",
  left: "4px",
  color: colours.th1l1,
};

// other table styles
// for feature list
export const tableCellNoWrapStyle = {
  whiteSpace: "nowrap",
};

export const tableCellNumberStyle = {
  paddingLeft: 0,
  fontVariantNumeric: "tabular-nums",
  textAlign: "right",
};

export const tableHeaderNumberStyle = {
  paddingLeft: 0,
  textAlign: "right",
};

export const areaHeaderStyle = {
  paddingLeft: 0,
  textAlign: "right",
};

// for summary tables
export const leftColumnPrimaryStyle = {
  background: colours.summaryHeader,
  color: colours.th1,
  fontSize: "14px",
  fontWeight: 600,
  padding: "8px",
  textAlign: "left",
};

export const leftColumnSecondaryStyle = {
  background: colours.white,
  fontSize: "14px",
  fontWeight: 400,
  padding: "8px",
  paddingLeft: "16px",
  textAlign: "left",
};

export const rightColumnPrimaryStyle = {
  background: colours.summaryHeader,
  paddingLeft: "20px",
  fontVariantNumeric: "tabular-nums",
  fontSize: "14px",
  fontWeight: 600,
  textAlign: "right",
};

export const rightColumnSecondaryStyle = {
  background: colours.white,
  paddingLeft: "20px",
  fontVariantNumeric: "tabular-nums",
  fontSize: "14px",
  fontWeight: 400,
  textAlign: "right",
};

export interface ITableRowData {
  rowData: any[];
  rowStyles?: any[];
  rowColspans?: any[];
  warningData?: any[];
  highlight?: boolean;
}

interface IProps {
  headers?: string[];
  headerStyles?: any[];
  rows: ITableRowData[];
  fixed?: boolean;
  onRowMouseEnter?: any;
  onRowMouseLeave?: any;
  getRowKey?: any;
}

const CompactTable = class extends React.Component<IProps, {}> {
  static defaultProps: {
    onRowMouseEnter: undefined;
    onRowMouseLeave: undefined;
    getRowKey: (row: any[]) => any;
  };

  render() {
    const {
      headers,
      headerStyles = [],
      rows,
      fixed,
      onRowMouseEnter,
      onRowMouseLeave,
      getRowKey,
    } = this.props;
    const anyHighlightedRows = rows.some((x: ITableRowData) => x.highlight);

    const renderWarningRows = (row: ITableRowData) => {
      return row.warningData && row.warningData.length ? (
        <WarningTableRow>
          <td colSpan={row.rowData.length}>
            {row.warningData.map((z: any[]) => {
              return (
                <Callout
                  key={`${row.rowData[0]}${z[0]}`}
                  type="warning"
                  inTable={true}
                >
                  <span style={{ marginRight: "6px" }}>{z[0]}</span>
                  {z[1] && (
                    <TextButtonWithArrow onClick={z[1]}>
                      Read more
                    </TextButtonWithArrow>
                  )}
                </Callout>
              );
            })}
          </td>
        </WarningTableRow>
      ) : null;
    };

    const renderHighlightCell = (highlighted: boolean | undefined) => {
      return (
        <TableCellHighlighted key={"highlight"}>
          {highlighted && <AlertIcon style={highlightIconStyle} />}
        </TableCellHighlighted>
      );
    };

    const renderCells = (row: ITableRowData) => {
      return row.rowData.map((y: string, index: number) => {
        let cellStyle = row.rowStyles ? row.rowStyles[index] : null;
        if (!cellStyle) {
          cellStyle = typeof y === "string" ? {} : buttonCellStyle;
        }
        let colspan = row.rowColspans ? row.rowColspans[index] : 1;
        return (
          <TableCell key={index} style={cellStyle} colSpan={colspan}>
            {y}
          </TableCell>
        );
      });
    };

    const renderStandardRow = (row: ITableRowData) => {
      const RowElement = row.highlight ? HighlightedTableRow : StandardTableRow;
      return (
        <React.Fragment key={getRowKey(row.rowData)}>
          <RowElement>
            {anyHighlightedRows && renderHighlightCell(row.highlight)}
            {renderCells(row)}
          </RowElement>
          {renderWarningRows(row)}
        </React.Fragment>
      );
    };

    const renderHoverRow = (row: ITableRowData) => {
      return (
        <React.Fragment key={getRowKey(row.rowData)}>
          <HoverTableRow
            onMouseEnter={() => onRowMouseEnter(getRowKey(row.rowData))}
            onMouseLeave={() => onRowMouseLeave(getRowKey(row.rowData))}
          >
            {anyHighlightedRows && renderHighlightCell(row.highlight)}
            {renderCells(row)}
          </HoverTableRow>
          {renderWarningRows(row)}
        </React.Fragment>
      );
    };

    return (
      <TableContainer>
        <table
          style={{
            width: "100%",
            tableLayout: fixed ? "fixed" : "auto",
            borderCollapse: "collapse",
          }}
        >
          {headers && (
            <thead>
              <tr>
                {anyHighlightedRows && <TableHeader key={"highlight"} />}
                {headers.map((x: string, index: number) => (
                  <TableHeader key={index} style={headerStyles[index] || {}}>
                    {x}
                  </TableHeader>
                ))}
              </tr>
            </thead>
          )}
          <TableBody>
            {rows.map((x: any) => {
              return !!onRowMouseEnter
                ? renderHoverRow(x)
                : renderStandardRow(x);
            })}
          </TableBody>
        </table>
      </TableContainer>
    );
  }
};

CompactTable.defaultProps = {
  onRowMouseEnter: undefined,
  onRowMouseLeave: undefined,
  getRowKey: (row: any[]) => row[0],
};

export default CompactTable;
